import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, ClipboardCheck, Grid3x3, Calendar,
  GraduationCap, Users, Settings, LogOut, Menu, X, Target, Database, ListChecks, ShieldAlert,
} from "lucide-react";
import { useState } from "react";

function navForRole(role) {
  const isEmployee = role === "employee";
  const isReadOnly = role === "quality" || role === "management";
  const isHR = role === "hr" || role === "super_admin";
  const testsLabel = isHR || isReadOnly ? "Test Register" : "My Tests";

  const employeeNav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/my-card", label: "My Card", icon: Target },
    { to: "/my-tests", label: testsLabel, icon: ClipboardCheck },
  ];
  if (isEmployee) return employeeNav;

  // Staff + operator tracks (shared between HR/SuperAdmin and read-only roles).
  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/my-card", label: "My Card", icon: Target },
    { to: "/my-tests", label: testsLabel, icon: ClipboardCheck },
    { section: "STAFF" },
    { to: "/tni-matrix/staff",    label: "TNI Matrix — Staff",     icon: Grid3x3 },
    { to: "/skill-matrix/staff",  label: "Competency Matrix",       icon: Grid3x3 },
    { to: "/employees/staff",     label: "Employee List — Staff",  icon: Users },
  ];
  if (isHR) nav.push({ to: "/assign-test/staff", label: "Assign Staff Test", icon: ClipboardCheck });
  nav.push({ section: "OPERATORS" });
  nav.push({ to: "/tni-matrix/operators",    label: "TNI Matrix — Operators", icon: Grid3x3 });
  nav.push({ to: "/skill-matrix/operators",  label: "Skill Matrix",            icon: Grid3x3 });
  nav.push({ to: "/employees/operators",     label: "Employee List — Operators", icon: Users });
  if (isHR) nav.push({ to: "/assign-test/operators", label: "Assign Operator Test", icon: ClipboardCheck });

  nav.push({ section: "SHARED" });
  nav.push({ to: "/calendar",  label: "Calendar",  icon: Calendar });
  nav.push({ to: "/trainings", label: "Trainings", icon: GraduationCap });
  if (isHR) nav.push({ to: "/competency-map", label: "Competency Map", icon: ListChecks });
  if (role === "super_admin") {
    nav.push({ to: "/data-import", label: "Data Import", icon: Database });
    nav.push({ to: "/data-audit", label: "Data Audit", icon: ShieldAlert });
    nav.push({ to: "/config",      label: "Settings",    icon: Settings });
  }
  return nav;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = navForRole(user?.system_role);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#FFFFFF]">
      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between border-b border-[#E5E5E5] px-4 h-14 bg-white">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#C41E1E] flex items-center justify-center rounded-sm">
            <span className="text-white font-bold text-sm">B</span>
          </div>
          <span className="font-heading font-bold tracking-tight">BLACKCAT TMS</span>
        </Link>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={`${open ? "block" : "hidden"} md:block md:w-[250px] md:min-h-screen border-r border-[#E5E5E5] bg-[#0D0D0D] text-white md:sticky md:top-0`}
        data-testid="app-sidebar"
      >
        <div className="hidden md:flex items-center gap-3 px-6 h-16 border-b border-neutral-800">
          <div className="w-8 h-8 bg-[#C41E1E] flex items-center justify-center rounded-sm">
            <span className="text-white font-black">B</span>
          </div>
          <div>
            <div className="font-heading font-bold tracking-tight text-sm leading-none">BLACKCAT</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mt-1">TMS v2</div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-neutral-800">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">Signed in as</div>
          <div className="mt-1 font-semibold text-sm">{user?.name}</div>
          <div className="text-xs text-neutral-400">{ROLE_LABELS[user?.system_role]} · #{user?.employee_id}</div>
        </div>

        <nav className="py-3">
          {items.map((it, idx) => {
            if (it.section) {
              return (
                <div
                  key={`section-${idx}`}
                  className="px-6 pt-4 pb-1 text-[9px] uppercase tracking-[0.24em] text-neutral-500"
                  data-testid={`nav-section-${it.section.toLowerCase()}`}
                >
                  {it.section}
                </div>
              );
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-").replace(/[—–]/g, "").replace(/-+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-[#C41E1E] text-white border-l-2 border-[#C9A84C]"
                      : "text-neutral-300 hover:bg-neutral-900 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <it.icon size={16} />
                {it.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute md:relative md:mt-6 left-0 right-0 px-6 py-4 border-t border-neutral-800">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-neutral-300 hover:text-white"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1">{children}</div>
        <footer className="border-t border-[#E5E5E5] px-4 md:px-10 py-3 text-[11px] text-[#666666] flex items-center justify-between" data-testid="app-footer">
          <span>Bajaj Communications Pvt Ltd &middot; Training Management System</span>
          <span className="text-[#999999]">IATF 16949 &middot; Clause 7.2</span>
        </footer>
      </main>
    </div>
  );
}
